import React, { useState, useEffect, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AiOutlineCopy, AiOutlineReload, AiOutlineDisconnect, AiOutlineLink, AiOutlineCheck } from 'react-icons/ai';
import { SiAnthropic } from 'react-icons/si';
import myAxios from '../AxiosUrl';
import styles from './ClaudeMcp.module.css';

const READ_TOOLS = [
    ['Dashboard summary', 'orders, revenue, profit, returns and visitors for today, yesterday, this month, last month'],
    ['Sales report', 'daily / weekly / monthly orders, revenue, units, profit, cancellations and returns for any date range'],
    ['Product performance', 'units, orders and revenue per product for a period, with current stock'],
    ['Order breakdown', 'orders by status, city, traffic source or payment type'],
    ['Customer insights', 'new vs returning customers, repeat buyers, top customers by spend'],
    ['Orders', 'list and search orders by status, customer, invoice or date range; open one order'],
    ['Products', 'list, search and open products; top selling and low stock'],
    ['Customers', 'customer analytics by period and customer search'],
    ['Catalog', 'categories, brands, contact messages'],
    ['Finance', 'expenses, purchases, cash, inventory, payables, receivables, balance sheet, profit & loss'],
    ['Marketing & SEO', 'banners, landing pages, blogs, SEO settings, FAQs, reviews'],
    ['Meta Analytics', 'ad spend, results, campaigns and page followers from your connected Meta token'],
    ['Facebook Page', 'posts and their views, clicks and reactions, Page insights, comments (and which still need a reply)'],
    ['Messenger', 'inbox conversations, unread chats and full message history'],
    ['Meta ads', 'campaigns, ad sets and ads with results, breakdowns by age, gender, region, placement and hour, pixel events, audiences, lead forms'],
    ['Messenger & WhatsApp automation', 'all reply settings, chats and their messages, comment replies, WhatsApp order updates with delivered / read status, and a test chat that sends nothing'],
    ['Account', 'team members, notifications, support tickets'],
];

const WRITE_TOOLS = [
    ['Orders', 'create a manual order, edit customer/address/amounts, change status (one or many), book Steadfast courier'],
    ['Products', 'create a product, update price, stock, name, code, flags and SEO fields'],
    ['Catalog', 'create categories and brands, add FAQs, approve or hide reviews'],
    ['Finance', 'record expenses and stock purchases'],
    ['Customers & messages', 'add a customer, send an SMS, update contact message status'],
    ['Content', 'write and update blog posts, update home page SEO'],
    ['Account', 'mark notifications read, open a support ticket, pick the Meta ad account / page'],
    ['Facebook & Messenger', 'post or schedule on your Page, edit a post, reply to or hide comments, reply in Messenger'],
    ['Meta ads', 'create campaigns, ad sets and ads (always paused first), boost a post, pause / start, change budget'],
    ['Messenger & WhatsApp automation', 'change switches and reply texts, add or edit keyword replies, connect, send templates to Meta, send an order\'s WhatsApp update, reply in a chat, pause the bot'],
];

const EXAMPLES = [
    'How were my sales this month compared to last month?',
    'Which products sold best in October, and which are low on stock?',
    'What share of orders came from Facebook last week?',
    'List my cancelled orders from the last 7 days.',
    'How much did I spend on Meta ads last week and what did it bring?',
    'Which Facebook comments and Messenger chats are still waiting for a reply?',
    'Are my WhatsApp order templates approved yet? Was the last order update delivered?',
    'Create an order for Rahim, 01711000000, Mirpur, 2 × product X.',
    'Mark these three orders as Courier Done.',
    'Boost my best post from this week for 3 days in Dhaka, women 20–40.',
];

const fmtDate = (v) => (v ? new Date(v).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

function ClaudeMcp() {
    const [access, setAccess] = useState(null);
    const [loading, setLoading] = useState(true);
    const [working, setWorking] = useState(false);
    const [freshUrl, setFreshUrl] = useState('');
    const [copied, setCopied] = useState(false);

    const errMsg = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await myAxios.get('mcp-access');
            setAccess(res.data?.access || { enabled: false });
        } catch (err) {
            setAccess({ enabled: false });
            toast.error(errMsg(err, 'Could not load connector status'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const generate = async () => {
        if (access?.enabled && !window.confirm('Generate a new URL? The current one stops working immediately.')) return;
        try {
            setWorking(true);
            const res = await myAxios.post('mcp-access/rotate');
            setAccess(res.data?.access || { enabled: true });
            setFreshUrl(res.data?.url || '');
            setCopied(false);
            toast.success('Connector URL ready');
        } catch (err) {
            toast.error(errMsg(err, 'Could not generate a connector URL'));
        } finally {
            setWorking(false);
        }
    };

    const revoke = async () => {
        if (!window.confirm('Revoke the connector URL? Claude will lose access until you generate a new one.')) return;
        try {
            setWorking(true);
            await myAxios.delete('mcp-access');
            setAccess({ enabled: false, writeEnabled: false });
            setFreshUrl('');
            toast.success('Connector revoked');
        } catch (err) {
            toast.error(errMsg(err, 'Could not revoke'));
        } finally {
            setWorking(false);
        }
    };

    const toggleWrite = async () => {
        const next = !access?.writeEnabled;
        if (next && !window.confirm('Allow Claude to create and update data in your store (orders, products, expenses, SMS…)? Claude will still ask you before sensitive actions, and can never delete anything.')) return;
        try {
            setWorking(true);
            const res = await myAxios.post('mcp-access/settings', { writeEnabled: next });
            setAccess(res.data?.access || { ...access, writeEnabled: next });
            toast.success(next ? 'Changes allowed. Reconnect in Claude to see the new tools.' : 'Read-only again');
        } catch (err) {
            toast.error(errMsg(err, 'Could not save'));
        } finally {
            setWorking(false);
        }
    };

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(freshUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 2500);
        } catch (e) {
            toast.error('Copy failed. Select the URL and copy it manually.');
        }
    };

    const writeOn = !!access?.writeEnabled;

    return (
        <div className={styles.container}>
            <ToastContainer position="top-right" autoClose={3000} />

            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <div className={styles.headerIcon}>
                        <SiAnthropic />
                    </div>
                    <div>
                        <h1 className={styles.title}>Claude AI (MCP)</h1>
                        <p className={styles.subtitle}>
                            Let Claude read your store data, answer questions about sales, stock, customers and ads, and
                            (optionally) make changes for you
                        </p>
                    </div>
                </div>
            </div>

            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>Connector</h2>
                    {!loading && (
                        <div className={styles.cardActions}>
                            <button type="button" className={styles.btnPrimary} onClick={generate} disabled={working}>
                                <AiOutlineReload /> {access?.enabled ? 'Generate new URL' : 'Generate connector URL'}
                            </button>
                            {access?.enabled && (
                                <button type="button" className={styles.btnDanger} onClick={revoke} disabled={working}>
                                    <AiOutlineDisconnect /> Revoke
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className={`${styles.skeleton} ${styles.skeletonLine}`} />
                ) : (
                    <div className={styles.statusGrid}>
                        <div>
                            <span className={styles.label}>Status</span>
                            <span className={`${styles.pill} ${access?.enabled ? styles.pillGreen : styles.pillGrey}`}>
                                {access?.enabled ? 'Active' : 'Not set up'}
                            </span>
                        </div>
                        <div>
                            <span className={styles.label}>Access</span>
                            <span className={`${styles.pill} ${!access?.enabled ? styles.pillGrey : writeOn ? styles.pillYellow : styles.pillGreen}`}>
                                {!access?.enabled ? '—' : writeOn ? 'Read + changes' : 'Read-only'}
                            </span>
                        </div>
                        <div>
                            <span className={styles.label}>Key</span>
                            <span className={styles.value}>{access?.enabled ? `${access.prefix}…` : '—'}</span>
                        </div>
                        <div>
                            <span className={styles.label}>Last used</span>
                            <span className={styles.value}>
                                {access?.enabled ? fmtDate(access.lastUsedAt) : '—'}
                                {access?.enabled && access.calls ? <span className={styles.muted}> · {access.calls} requests</span> : null}
                            </span>
                        </div>
                    </div>
                )}

                {freshUrl && (
                    <div className={styles.urlBox}>
                        <span className={styles.label}>Your connector URL (shown once)</span>
                        <div className={styles.urlRow}>
                            <input className={styles.urlInput} readOnly value={freshUrl} onFocus={(e) => e.target.select()} />
                            <button type="button" className={styles.btnSecondary} onClick={copy}>
                                {copied ? <AiOutlineCheck /> : <AiOutlineCopy />} {copied ? 'Copied' : 'Copy'}
                            </button>
                        </div>
                        <p className={styles.warn}>
                            Treat this like a password: anyone with the URL can use your store data. We only keep a fingerprint,
                            so if you lose it, generate a new one.
                        </p>
                    </div>
                )}

                {!freshUrl && access?.enabled && (
                    <p className={styles.hint}>
                        The full URL is only shown when it is generated. If you need it again, generate a new one and update it in
                        Claude.
                    </p>
                )}

                {!loading && access?.enabled && (
                    <div className={styles.switchRow}>
                        <button
                            type="button"
                            role="switch"
                            aria-checked={writeOn}
                            className={`${styles.switch} ${writeOn ? styles.switchOn : ''}`}
                            onClick={toggleWrite}
                            disabled={working}
                            title={writeOn ? 'Switch back to read-only' : 'Allow changes'}
                        />
                        <div className={styles.switchText}>
                            <span className={styles.switchTitle}>Allow Claude to make changes</span>
                            <span className={styles.switchDesc}>
                                Off = Claude can only read. On = Claude can also create orders, update products and stock,
                                change order status, record expenses, send SMS, write blog posts, post and reply on Facebook,
                                reply in Messenger, create and manage Meta ads and more. Nothing can ever be
                                deleted through Claude. After switching, reconnect the connector in Claude so it picks up the
                                new tools.
                            </span>
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.twoCol}>
                <div className={styles.card}>
                    <h2 className={styles.cardTitle}>Add to claude.ai</h2>
                    <ol className={styles.steps}>
                        <li>
                            Open <strong>claude.ai → Settings → Connectors</strong> and click <strong>Add custom connector</strong>.
                        </li>
                        <li>
                            Name it after your store and paste the connector URL. Leave the OAuth fields empty and click{' '}
                            <strong>Add</strong>.
                        </li>
                        <li>
                            In a chat, open the <strong>tools</strong> menu (the + or sliders icon) and switch the connector on.
                        </li>
                        <li>Ask away. Claude will call the store tools it needs and ask before sensitive changes.</li>
                    </ol>
                    <a className={styles.link} href="https://claude.ai/settings/connectors" target="_blank" rel="noreferrer">
                        <AiOutlineLink /> Open claude.ai connectors
                    </a>

                    <h3 className={styles.subTitle}>Claude Code</h3>
                    <pre className={styles.code}>claude mcp add --transport http my-store "YOUR_CONNECTOR_URL"</pre>
                    <p className={styles.hint}>Claude Desktop uses the same Connectors screen as claude.ai.</p>
                </div>

                <div className={styles.card}>
                    <h2 className={styles.cardTitle}>Try asking</h2>
                    <ul className={styles.examples}>
                        {EXAMPLES.map((q) => (
                            <li key={q}>“{q}”</li>
                        ))}
                    </ul>
                    <p className={styles.hint}>
                        Facebook, Messenger and Meta ads questions need a token connected on the <strong>Meta Analytics</strong> page
                        first. The last three examples need “Allow Claude to make changes”.
                    </p>
                </div>
            </div>

            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>What Claude can do</h2>
                    <span className={`${styles.pill} ${writeOn ? styles.pillYellow : styles.pillGreen}`}>
                        {writeOn ? 'Read + changes' : 'Read-only'}
                    </span>
                </div>
                <div className={styles.toolSection}>Read</div>
                <div className={styles.toolGrid}>
                    {READ_TOOLS.map(([name, desc]) => (
                        <div key={name} className={styles.toolItem}>
                            <span className={styles.toolName}>{name}</span>
                            <span className={styles.toolDesc}>{desc}</span>
                        </div>
                    ))}
                </div>
                <div className={styles.toolSection}>Changes {writeOn ? '(enabled)' : '(off — switch on above)'}</div>
                <div className={styles.toolGrid} style={{ opacity: writeOn ? 1 : 0.6 }}>
                    {WRITE_TOOLS.map(([name, desc]) => (
                        <div key={name} className={styles.toolItem}>
                            <span className={styles.toolName}>{name}</span>
                            <span className={styles.toolDesc}>{desc}</span>
                        </div>
                    ))}
                </div>
                <p className={styles.hint}>
                    Claude never sees passwords, payment keys or API secrets, and there is no way to delete orders, products or
                    anything else through Claude.
                </p>
            </div>
        </div>
    );
}

export default ClaudeMcp;
